import { useBrand } from '../lib/brand-context.js';
import { Icon } from './ui/Icon.js';
import { useState, useRef, useEffect } from 'react';
import type { Brand } from '../types/index.js';

/** Spec 076: render the lock state indicator next to a brand name. */
function LockBadge({ brand }: { brand: Brand }) {
  if (!brand.locked_by_email) return null;
  if (brand.is_locked_by_me) {
    return (
      <span
        className="relative ml-1 inline-flex items-center"
        title="Locked by you"
        aria-label="Locked by you"
        data-testid="brand-lock-owner"
      >
        <Icon name="bx-lock" className="text-violet-600" />
        <span className="absolute -right-0.5 -top-0.5 h-1.5 w-1.5 rounded-full bg-violet-600" />
      </span>
    );
  }
  return (
    <span
      className="ml-1 inline-flex items-center"
      title={`Locked — shared by ${brand.locked_by_email}`}
      aria-label="Locked (shared with you)"
      data-testid="brand-lock-member"
    >
      <Icon name="bx-lock" className="text-slate-400" />
    </span>
  );
}

export function BrandSelector() {
  const { brands, selectedBrand, selectBrand, loading } = useBrand();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  if (loading) {
    return (
      <div className="h-9 w-48 animate-pulse rounded-md bg-gray-200" />
    );
  }

  if (brands.length === 0) {
    return (
      <a
        href="/settings"
        className="text-sm text-violet-600 hover:text-violet-800"
      >
        Create your first brand
      </a>
    );
  }

  return (
    <div ref={ref} className="relative">
      {/* Spec 230: `data-testid="brand-selector"` is the stable anchor for gate
          200 and gate 229.A.5. Both used to find this control with
          `button:has(svg)`, which stopped meaning anything the moment its
          chevron became a Boxicon — Boxicons render `<i class="bx">` and no
          <svg> at all. A testid is the stronger anchor regardless: `has(svg)`
          would equally match any other icon-bearing button in the bar. */}
      <button
        data-testid="brand-selector"
        // Spec 247 (review P1-13): the label truncates at 180px ("Persona Seed
        // Brand (spec 1…"), so the full name is always one hover away.
        title={selectedBrand?.name}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen(!open)}
        className="flex items-center gap-2 rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
      >
        <span className="flex items-center max-w-[180px] truncate">
          {selectedBrand?.name ?? 'Select brand'}
          {selectedBrand && <LockBadge brand={selectedBrand} />}
        </span>
        <Icon name="bx-chevron-down" className={`transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        // Spec 257: a list of brands to choose from is a listbox of options,
        // not a column of buttons — what a screen reader announces, and why the
        // rows are full-width rows rather than pills.
        <div role="listbox" aria-label="Brands" data-testid="brand-selector-panel" className="absolute left-0 z-[100] mt-1 w-64 rounded-lg border border-slate-200 bg-white py-1">
          {brands.map((brand) => (
            <button
              type="button"
              role="option"
              aria-selected={selectedBrand?.id === brand.id}
              key={brand.id}
              onClick={() => {
                selectBrand(brand);
                setOpen(false);
              }}
              className={`flex w-full items-center px-4 py-2 text-left text-sm transition-colors ${
                selectedBrand?.id === brand.id
                  ? 'bg-violet-50 text-violet-700'
                  : 'text-gray-700 hover:bg-gray-50'
              }`}
            >
              <div className="flex-1">
                <div className="flex items-center font-medium">
                  {brand.name}
                  <LockBadge brand={brand} />
                </div>
                {brand.domain && (
                  <div className="text-xs text-gray-400">{brand.domain}</div>
                )}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
